import type { ComponentPropsWithoutRef } from 'react'
import type { MarketingPath } from '~/lib/marketing-paths'

type MarketingLinkProps = Omit<ComponentPropsWithoutRef<'a'>, 'href'> & {
  href: MarketingPath
}

/**
 * Links from an app route to the marketing surface. A plain `<a>` — a full
 * page load — never a router `<Link>`.
 *
 * Two reasons, in order of severity:
 *
 * 1. An in-router app → marketing navigation flips `routerState.location` to
 *    the marketing path immediately, while the app route it came from is
 *    still mounted (matches follow `resolvedLocation`, which lags by a layout
 *    effect). `RootDocument` keys the app-shell providers off the location,
 *    so the app route would re-render with `I18nProvider` yanked out from
 *    under it — `useLingui()` returns null and the route crashes.
 *    `RootDocument` now holds the providers for the whole transition, but a
 *    hard navigation means the window never opens in the first place.
 * 2. SPA-navigating into marketing keeps Convex, auth and Sentry loaded, which
 *    defeats the bundle split `getRouter()` exists to create. A fresh document
 *    boots the Convex-free marketing router instead.
 *
 * This is the mirror of the marketing → app guard in `getMarketingRouter()`
 * (`src/router.tsx`), which turns navigations in the other direction into
 * full page loads for the same reason.
 *
 * Renders no Lingui/toast/tooltip runtime, so it is safe on the provider-free
 * marketing branch of `RootDocument` — `RouteErrorComponent` needs that.
 */
export const MarketingLink = ({ href, ...props }: MarketingLinkProps) => (
  <a href={href} {...props} />
)
